import CompanyTemperatureMode, { isTemperatureRegister } from "../../components/CompanyTemperatureMode";
import EquipmentManager from "../../components/EquipmentManager";
import GroupedProcedures from "../../components/GroupedProcedures";
import { CopyCompanyEmailButton, CompanyAccessEmail, companyAccessEmail, UserSectionTabs } from "../../components/OnboardingUserActions";
import AdminRecordEditor from "../../components/AdminRecordEditor";
import { ProcedureRow } from "../../components/ProcedureEditor";
import ModuleSelector from "../../components/ModuleSelector";
import DailyReportText from "../../components/DailyReportText";
import { companyFields, siteFields, departmentFields, userFields } from "../../components/AdminConfigFields";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building2, MapPin, Wrench, ClipboardList, Users2, Boxes, PauseCircle, Plus, Trash2, ShieldCheck, Smartphone, Check, X, KeyRound, Eye, EyeOff, Eraser, AlertTriangle, Link2, Copy, RefreshCw, CheckCircle2, Ban, MessageCircle, Send, Mail, LogIn, FileText, Download, Upload, PenTool, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { formatItalianDateTime } from "../../lib/dateUtils";
import { Card, Button, Spinner, StatusBadge, Field, FieldHelp, Input, PasswordInput, Select, inputCls, PageGuide } from "../../components/ui-kit";
import { SignaturePad } from "../../components/SignaturePad";

const TABS = [
  { key: "dati", label: "Dati azienda", Icon: Building2 },
  { key: "sedi", label: "Sedi & reparti", Icon: MapPin },
  { key: "attrezzature", label: "Attrezzature", Icon: Wrench },
  { key: "procedure", label: "Procedure", Icon: ClipboardList },
  { key: "utenti", label: "Utenti", Icon: Users2 },
  { key: "moduli", label: "Servizio & moduli", Icon: Boxes },
  { key: "intake", label: "Modulo ricette cliente", Icon: Link2 },
  { key: "documenti-cliente", label: "Documenti cliente", Icon: FileText },
  { key: "sospensione", label: "Sospensione", Icon: PauseCircle },
  { key: "pulizia", label: "Pulizia dati", Icon: Eraser },
];

const TAB_GUIDES = {
  dati: {
    summary: "L'anagrafica dell'azienda: viene riusata nei documenti generati per il cliente, quindi vale la pena tenerla precisa.",
    goals: ["Tenere aggiornati i dati anagrafici e fiscali dell'azienda cliente."],
    steps: ["Tocca “Modifica” per aggiornare ragione sociale, dati fiscali o contatti.", "Tocca “Elimina” solo se l'azienda va rimossa definitivamente: cancella anche sedi, reparti, utenti e registrazioni collegate."],
  },
  sedi: {
    summary: "Sedi e reparti sono la struttura su cui si appoggiano attrezzature, procedure e utenti: vanno create per prime.",
    goals: ["Censire le sedi operative dell'azienda e i reparti in cui verranno svolti i controlli HACCP."],
    steps: [
      "Aggiungi una sede con nome e indirizzo.",
      "Per ogni sede, aggiungi i reparti in cui si svolgono i controlli (es. Cucina, Magazzino, Sala).",
      "Sedi e reparti creati qui compariranno nei selettori delle altre schede (Attrezzature, Procedure, Utenti).",
    ],
  },
  attrezzature: {
    summary: "Le attrezzature sono la base dei controlli quotidiani di temperatura e pulizia svolti dagli operatori.",
    goals: ["Registrare le attrezzature a temperatura controllata e quelle aziendali usate per pulizia/sanificazione."],
    steps: [
      "Scegli sede e reparto, poi il tipo di attrezzatura.",
      "Per le attrezzature a temperatura controllata, imposta l'intervallo minimo/massimo corretto: i controlli quotidiani segnaleranno una non conformità se il valore registrato esce da questo intervallo.",
    ],
  },
  procedure: {
    summary: "Le procedure sono i controlli veri e propri che gli operatori dell'azienda eseguiranno e registreranno ogni giorno.",
    goals: ["Configurare i controlli di autocontrollo (temperature, pulizie, ecc.) previsti dal piano HACCP dell'azienda."],
    steps: [
      "Imposta prima la modalità comune per i controlli di temperatura, se applicabile a tutti i reparti.",
      "Per ogni procedura scegli sede/reparto, frequenza, tipo di risposta e, se numerica, i limiti accettabili.",
      "Segna come CCP i punti critici del piano HACCP: vengono trattati con priorità massima in caso di non conformità.",
    ],
  },
  utenti: {
    summary: "Da qui crei gli accessi per chi lavorerà con l'app in azienda, con permessi diversi in base al ruolo.",
    goals: [
      "Creare gli accessi per titolare, responsabili, addetti e ispettori dell'azienda cliente.",
      "Limitare l'accesso di ogni addetto alle sole sedi e reparti di sua competenza.",
    ],
    steps: [
      "Titolare e Responsabile accedono con email e password; l'Addetto con l'email aziendale (uguale per tutti) e il suo PIN personale, che ne individua sedi e reparti; l'Ispettore in sola lettura.",
      "Assegna le sedi (e i reparti, per gli addetti) a cui la persona avrà accesso.",
      "Da qui puoi anche reimpostare PIN o password in caso di smarrimento, revocando le sessioni precedenti.",
    ],
  },
  moduli: {
    summary: "I moduli attivi determinano cosa vede e può usare l'azienda nell'app, in base all'accordo commerciale.",
    goals: ["Attivare o disattivare i moduli dell'app in base al livello di consulenza concordato con il cliente."],
    steps: [
      "Cambia il livello di consulenza se è cambiato l'accordo commerciale.",
      "Attiva o disattiva singolarmente ogni modulo: i dati già registrati restano conservati anche se disattivi un modulo.",
    ],
  },
  intake: {
    summary: "Questo è il modulo con cui il cliente inserisce da solo le proprie RICETTE (più i fornitori e gli ingredienti che gli servono per costruirle), prima ancora di avere le credenziali dell'app: tu controlli e importi con un click.",
    goals: ["Far arrivare il cliente alla consegna con le ricette già censite nell'app (ingredienti e fornitori inclusi), senza doverle inserire tu manualmente una per una."],
    steps: [
      "Genera il link e condividilo su WhatsApp con un click: usa il numero salvato nei contatti dell'azienda. Non scade e il cliente può riaprirlo quante volte vuole, anche dopo che hai già importato dei dati.",
      "Il modulo ha 3 sezioni — Fornitori, Ingredienti, Ricette — ma se il cliente ha già l'app con fornitori/materie prime caricati può andare dritto a Ricette: lì trova già selezionabili le materie prime esistenti, e può aggiungerne una nuova al volo se gli manca senza dover tornare indietro.",
      "Quando il cliente ha inserito dei dati, controlla qui l'anteprima delle ricette (e di fornitori/ingredienti collegati): le voci già presenti nell'app sono segnalate come “già presente”.",
      "Deseleziona quello che non vuoi importare e premi “Importa”: le ricette (con i relativi fornitori e ingredienti) compaiono in bozza nella scheda Ricette dell'app del cliente, pronte perché tu le completi (procedura, conservazione, allergeni) e le approvi. Puoi importare più volte man mano che il cliente aggiunge altre ricette.",
      "Se vuoi togliere subito l'accesso al cliente (es. hai già consegnato l'app), premi “Blocca link”: potrai sempre riattivarlo generandone uno nuovo.",
    ],
  },
  "documenti-cliente": {
    summary: "Manda al cliente un link per allegare firma e documenti anagrafici, senza bisogno di credenziali: solo la firma è obbligatoria, il resto puoi sollecitarlo in seguito.",
    goals: ["Raccogliere firma, documento d'identità, codice fiscale, visura camerale, planimetria e relazione tecnica direttamente dal cliente."],
    steps: [
      "Genera il link e condividilo su WhatsApp con un click: usa il numero salvato nei contatti dell'azienda.",
      "Il cliente può caricare i documenti quando vuole e tornare più volte: solo la firma blocca l'invio, tutto il resto è facoltativo.",
      "Quando il cliente invia il modulo con documenti mancanti, lo vedi qui e nella dashboard: puoi rigenerare il link e rimandarlo su WhatsApp con un click.",
    ],
  },
  sospensione: {
    summary: "Usa questa scheda per gestire temporaneamente l'accesso dell'azienda, ad esempio in caso di mancato pagamento.",
    goals: ["Gestire in modo reversibile l'accesso dell'azienda senza perdere alcun dato registrato."],
    steps: [
      "“Limitata” lascia 3 giorni di accesso prima del blocco automatico: utile come primo avviso.",
      "“Bloccata” sospende subito l'accesso; i dati restano comunque conservati.",
      "Riporta lo stato su “Attiva” per riattivare l'accesso normale in qualunque momento.",
    ],
  },
  pulizia: {
    summary: "Azzera i dati trascrizionali del cliente (ricette, produzioni, controlli...) quando lo richiede esplicitamente, senza toccare la configurazione di base.",
    goals: ["Ripulire una o più aree su richiesta del cliente, in modo tracciato e irreversibile."],
    steps: [
      "Spunta una per una solo le aree da pulire: non esiste un pulsante per cancellarle tutte insieme.",
      "Ricette, materie prime, lotti, fornitori e attrezzature sono l'archivio di base del cliente: la conferma lo segnala con un avviso in più.",
      "Conferma digitando esattamente il nome dell'azienda: è l'ultima protezione prima della cancellazione definitiva.",
      "Utenti, sedi, reparti, procedure e log di audit non vengono mai toccati da questa scheda.",
    ],
  },
};

export default function TenantDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState(TABS.some((tb) => tb.key === searchParams.get("tab")) ? searchParams.get("tab") : "dati");
  const [accessLinks, setAccessLinks] = useState({});
  const [fullAccess, setFullAccess] = useState({});

  const load = useCallback(() => api.get(`/admin/tenants/${id}`).then(({ data }) => setD(data)).catch(() => toast.error("Errore caricamento")), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Spinner />;
  const t = d.tenant;

  return (
    <div className="space-y-6" data-testid="tenant-detail">
      <button onClick={() => nav("/admin")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="w-4 h-4" /> Tutte le aziende</button>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-2xl bg-navy grid place-items-center text-gold-bright font-heading text-2xl font-bold">{t.name[0]}</div>
        <div>
          <h1 className="font-heading text-3xl font-bold">{t.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={t.status === "active" ? "verde" : t.status === "limited" ? "giallo" : "rosso"}>{t.status}</StatusBadge>
            <span className="text-sm text-slate-400">Consulenza {t.plan} · {d.sites.length} sedi · {d.users.length} utenti</span>
          </div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`tab-${tb.key}`}
            className={`inline-flex shrink-0 items-center gap-2 px-4 py-2.5 rounded-lg text-sm whitespace-nowrap min-h-[44px] ${tab === tb.key ? "bg-navy text-white" : "bg-white border border-hairline text-slate-600 hover:bg-slate-50"}`}>
            <tb.Icon className="w-4 h-4" /> {tb.label}
          </button>
        ))}
      </div>

      <PageGuide title={`Guida rapida: ${TABS.find((tb) => tb.key === tab)?.label}`} {...TAB_GUIDES[tab]} testid="tenant-detail-guide" />

      {tab === "dati" && <DatiTab t={t} id={id} reload={load} onDeleted={() => nav("/admin")} />}
      {tab === "sedi" && <SediTab id={id} d={d} reload={load} />}
      {tab === "attrezzature" && <AttrezzatureTab id={id} d={d} reload={load} />}
      {tab === "procedure" && <ProcedureTab id={id} d={d} reload={load} />}
      {tab === "utenti" && <UtentiTab id={id} d={d} reload={load} accessLinks={accessLinks} setAccessLinks={setAccessLinks} fullAccess={fullAccess} setFullAccess={setFullAccess} />}
      {tab === "moduli" && <ModuliTab id={id} d={d} reload={load} />}
      {tab === "intake" && <IntakeTab id={id} t={t} />}
      {tab === "documenti-cliente" && <ClientDocsTab id={id} t={t} />}
      {tab === "sospensione" && <SospensioneTab id={id} t={t} reload={load} />}
      {tab === "pulizia" && <PuliziaTab id={id} t={t} />}
    </div>
  );
}

function DatiTab({ t, id, reload, onDeleted }) {
  return (
    <Card className="p-6" data-testid="dati-tab"><AdminRecordEditor record={t} endpoint={`/admin/tenants/${id}`} fields={companyFields} reload={reload} onDeleted={onDeleted} prepare={(draft) => ({ name: draft.name, fiscal: draft.fiscal, contacts: draft.contacts })} deleteMessage={`Eliminare definitivamente l’azienda “${t.name}” e tutti i suoi dati? Questa operazione non è annullabile.`}><div className="grid sm:grid-cols-2 gap-4">
      <Info label="Ragione sociale" value={t.name} />
      <Info label="Livello di consulenza" value={t.plan} />
      <Info label="P.IVA" value={t.fiscal?.vat || "—"} />
      <Info label="Codice fiscale" value={t.fiscal?.codice_fiscale || "—"} />
      <Info label="PEC / SDI" value={`${t.fiscal?.pec || "—"} · ${t.fiscal?.sdi || "—"}`} />
      <Info label="Email" value={t.contacts?.email || "—"} />
      <Info label="Telefono" value={t.contacts?.phone || "—"} />
      <Info label="WhatsApp" value={t.contacts?.whatsapp ? `${t.contacts.whatsapp}${t.contacts.whatsapp_owner ? ` (${t.contacts.whatsapp_owner})` : ""}` : "—"} />
      <Info label="Indirizzo" value={t.contacts?.address || "—"} /></div></AdminRecordEditor>
    </Card>
  );
}
const Info = ({ label, value }) => (
  <div><div className="text-xs uppercase tracking-wider text-slate-400 font-body">{label}</div><div className="text-navy font-body mt-0.5">{value}</div></div>
);

function SediTab({ id, d, reload }) {
  const [name, setName] = useState(""); const [addr, setAddr] = useState(""); const [vehiclesOnly, setVehiclesOnly] = useState(false);
  const [depName, setDepName] = useState(""); const [depSite, setDepSite] = useState(d.sites[0]?.id || "");
  const addSite = async () => { if (!name) return; try { await api.post(`/admin/tenants/${id}/sites`, { name, address: addr, vehicles_only: vehiclesOnly, opening_hours: { mon: true, tue: true, wed: true, thu: true, fri: true, sat: true, sun: true } }); setName(""); setAddr(""); setVehiclesOnly(false); toast.success("Sede creata"); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const addDep = async () => { if (!depName || !depSite) return; try { await api.post(`/admin/tenants/${id}/departments`, { site_id: depSite, name: depName }); setDepName(""); toast.success("Reparto creato"); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="sedi-tab">
      <Card className="p-6">
        <h3 className="font-heading font-semibold mb-4">Sedi</h3>
        <div className="space-y-2 mb-4">
          {d.sites.map((s) => (
            <div key={s.id} className="p-3 rounded-lg border border-hairline">
              <AdminRecordEditor record={s} endpoint={`/admin/tenants/${id}/sites/${s.id}`} fields={siteFields} reload={reload}><div className="font-medium text-navy">{s.name}{s.vehicles_only && <span className="ml-2 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">Solo automezzi</span>}</div>
              <div className="text-xs text-slate-400">{s.address}</div></AdminRecordEditor>
              <div className="mt-2 flex flex-wrap gap-1">
                {d.departments.filter((x) => x.site_id === s.id).map((x) => <span key={x.id} className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{x.name}</span>)}
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-2 pt-4 border-t border-hairline">
          <Input placeholder="Nome sede" value={name} onChange={(e) => setName(e.target.value)} data-testid="site-name" />
          <Input placeholder="Indirizzo" value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="site-addr" />
          <label className="flex min-h-[44px] items-center gap-2 text-sm"><input type="checkbox" checked={vehiclesOnly} onChange={(e) => setVehiclesOnly(e.target.checked)} className="h-5 w-5 accent-navy" data-testid="site-vehicles-only" />Solo automezzi (consegne o food truck, nessun locale fisso)</label>
          <Button variant="success" className="w-full" onClick={addSite} data-testid="add-site"><Plus className="w-4 h-4" /> Aggiungi sede</Button>
        </div>
      </Card>
      <Card className="p-6">
        <h3 className="font-heading font-semibold mb-4">Reparti</h3><div className="mb-4 space-y-3">{d.departments.map((dep) => <div key={dep.id} className="rounded-lg border border-hairline p-3"><AdminRecordEditor record={dep} endpoint={`/admin/tenants/${id}/departments/${dep.id}`} fields={departmentFields} reload={reload}><p className="font-medium">{dep.name}</p><p className="text-sm text-slate-500">{d.sites.find((site) => site.id === dep.site_id)?.name}</p></AdminRecordEditor></div>)}</div>
        <div className="space-y-2">
          <Select value={depSite} onChange={(e) => setDepSite(e.target.value)} data-testid="dep-site">{d.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
          <Input placeholder="Nome reparto" value={depName} onChange={(e) => setDepName(e.target.value)} data-testid="dep-name" />
          <Button variant="success" className="w-full" onClick={addDep} data-testid="add-dep"><Plus className="w-4 h-4" /> Aggiungi reparto</Button>
        </div>
      </Card>
    </div>
  );
}

function AttrezzatureTab({ id, d, reload }) {
  return <div data-testid="attr-tab"><EquipmentManager tenantId={id} equipment={d.equipment} sites={d.sites} departments={d.departments} reload={reload} /></div>;
}

function ProcedureTab({ id, d, reload }) {
  const [f, setF] = useState({ site_id: d.sites[0]?.id || "", name: "", module: "temperature", response_type: "conforme", temperature_mode: "general", is_ccp: false, limit_min: "", limit_max: "", frequency: "daily", instructions: "", corrective_action: "" });
  const add = async () => {
    if (!f.name) return;
    try {
      await api.post(`/admin/tenants/${id}/procedures`, { ...f, limit_min: f.limit_min === "" ? null : parseFloat(f.limit_min), limit_max: f.limit_max === "" ? null : parseFloat(f.limit_max) });
      toast.success("Procedura creata"); setF({ ...f, name: "", instructions: "" }); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]" data-testid="proc-tab">
      <Card className="p-6">
        <CompanyTemperatureMode tenantId={id} procedures={d.procedures} savedMode={d.tenant.temperature_mode} reload={reload} />
        <h3 className="font-heading font-semibold mb-4">Procedure configurate</h3>
        <p className="mb-4 text-sm text-slate-500">Le procedure sono raggruppate per sede e reparto. Lo stesso controllo può essere previsto in più reparti.</p>
        <GroupedProcedures procedures={d.procedures} sites={d.sites} departments={d.departments} renderProcedure={(p) => (
          <ProcedureRow procedure={p} d={d} tenantId={id} reload={reload} />
        )} />
      </Card>
      <Card className="p-6 space-y-2">
        <h3 className="font-heading font-semibold mb-2">Nuova procedura</h3>
        <Select value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })} data-testid="proc-site">{d.sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
        <Input placeholder="Nome procedura" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="proc-name" />
        <div className="grid grid-cols-2 gap-2">
          <Select value={f.module} onChange={(e) => setF({ ...f, module: e.target.value })}><option value="temperature">Temperature</option><option value="pulizie">Pulizie</option></Select>
          <Select disabled={f.module === "temperature"} value={f.module === "temperature" ? (d.tenant.temperature_mode || d.procedures.find((p) => p.active && isTemperatureRegister(p))?.temperature_mode || "general") : f.temperature_mode} onChange={(e) => setF({ ...f, temperature_mode: e.target.value, response_type: e.target.value === "general" ? "conforme" : "numeric" })} data-testid="proc-mode"><option value="analytic">Analitica</option><option value="general">Check di conformità</option><option value="standard">Controllo standard</option></Select>
        </div>
        {(d.tenant.temperature_mode || f.temperature_mode) === "analytic" && (
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="Limite min" value={f.limit_min} onChange={(e) => setF({ ...f, limit_min: e.target.value })} data-testid="proc-min" />
            <Input type="number" placeholder="Limite max" value={f.limit_max} onChange={(e) => setF({ ...f, limit_max: e.target.value })} data-testid="proc-max" />
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.is_ccp} onChange={(e) => setF({ ...f, is_ccp: e.target.checked })} className="w-5 h-5 accent-[#0A1F44]" data-testid="proc-ccp" /> Classifica come CCP (punto critico)</label>
        {f.module === "temperature" && <p className="text-sm text-slate-500">La modalità si imposta dal comando “Modalità comune a tutti i reparti”.</p>}
        <Input placeholder="Istruzioni (facoltativo)" value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} />
        <Input placeholder="Azione correttiva (facoltativo)" value={f.corrective_action} onChange={(e) => setF({ ...f, corrective_action: e.target.value })} />
        <Button variant="success" className="w-full" onClick={add} data-testid="add-proc"><Plus className="w-4 h-4" /> Crea procedura</Button>
      </Card>
    </div>
  );
}

function UtentiTab({ id, d, reload, accessLinks, setAccessLinks, fullAccess, setFullAccess }) {
  const [f, setF] = useState({ name: "", email: "", role: "titolare", password: "", pin: "", site_ids: [], haccp_self_control_manager: false, is_employer: false });
  // Schede separate: titolare/responsabili (e ispettori) oppure addetti, mai insieme
  const [group, setGroup] = useState("owners");
  const inGroup = (u) => (group === "workers" ? u.role === "addetto" : u.role !== "addetto");
  const activeUsers = d.users.filter((u) => u.active !== false);
  const changeGroup = (next) => {
    setGroup(next);
    setF({ name: "", email: "", role: next === "workers" ? "addetto" : "titolare", password: "", pin: "", site_ids: [], haccp_self_control_manager: false, is_employer: false });
  };
  const [signature, setSignature] = useState(null);
  const [signatureMode, setSignatureMode] = useState("draw"); // "draw" | "preview", per il form "Nuovo utente"
  const [hasClientSignature, setHasClientSignature] = useState(false);
  const [signatureBusy, setSignatureBusy] = useState(null);
  const [newUserSignatureBusy, setNewUserSignatureBusy] = useState(false);
  const uploadNewUserSignatureFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setSignature(reader.result); setSignatureMode("preview"); };
    reader.readAsDataURL(file);
  };
  const importNewUserSignatureFromDocs = async () => {
    setNewUserSignatureBusy(true);
    try {
      const { data: blob } = await api.get(`/admin/tenants/${id}/client-docs/signature/file`, { responseType: "blob" });
      const reader = new FileReader();
      reader.onload = () => { setSignature(reader.result); setSignatureMode("preview"); toast.success("Firma importata dai documenti ricevuti"); };
      reader.readAsDataURL(blob);
    } catch (e) { toast.error("Nessuna firma ricevuta dal modulo documenti per questa azienda"); }
    finally { setNewUserSignatureBusy(false); }
  };
  const resetNewUserSignature = () => { setSignature(null); setSignatureMode("draw"); };
  useEffect(() => {
    api.get(`/admin/tenants/${id}/client-docs`)
      .then(({ data }) => setHasClientSignature(Boolean(data.fields?.find((f) => f.key === "signature")?.present)))
      .catch(() => setHasClientSignature(false));
  }, [id]);
  const uploadSignature = async (userId, file) => {
    if (!file) return;
    setSignatureBusy(userId);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/admin/tenants/${id}/users/${userId}/signature/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Firma caricata");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSignatureBusy(null); }
  };
  const importSignatureFromDocs = async (userId) => {
    setSignatureBusy(userId);
    try {
      await api.post(`/admin/tenants/${id}/users/${userId}/signature/from-client-docs`);
      toast.success("Firma importata dai documenti ricevuti");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSignatureBusy(null); }
  };
  const [pinResetUser, setPinResetUser] = useState(null);
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [resettingPin, setResettingPin] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [creatingLink, setCreatingLink] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const impersonate = async (u) => {
    setImpersonating(u.id);
    try {
      await api.post(`/admin/tenants/${id}/users/${u.id}/impersonate`);
      window.location.href = "/app";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setImpersonating(null);
    }
  };
  const createAccessLink = async (u) => {
    setCreatingLink(u.id);
    try {
      const { data } = await api.post(`/admin/tenants/${id}/users/${u.id}/access-link`);
      const link = `${window.location.origin}/accesso-diretto?token=${encodeURIComponent(data.token)}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      setAccessLinks((current) => ({ ...current, [u.id]: link }));
      toast.success("Nuovo link copiato: valido 30 minuti, monouso");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCreatingLink(null); }
  };
  const waNumber = (raw) => (raw || "").replace(/\D/g, "");
  const [sendingFull, setSendingFull] = useState(null);
  const genPassword = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    let out = "";
    for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
    return out;
  };
  const fullAccessMessage = (u, info) =>
    `Ciao ${u.name}, ecco i dati per accedere a DF HACCP.\n\n` +
    `Link rapido (valido 30 minuti, un solo utilizzo): ${info.link}\n\n` +
    `Se il link è scaduto, apri il sito e accedi con:\n` +
    `Email: ${u.email}\nPassword: ${info.password}`;
  const sendFullAccess = async (u) => {
    if (!window.confirm(`Generare una nuova password per ${u.name}? Quella attuale smetterà subito di funzionare e le sessioni aperte verranno chiuse.`)) return;
    setSendingFull(u.id);
    try {
      const password = genPassword();
      await api.post(`/admin/tenants/${id}/users/${u.id}/reset-password`, { password });
      const { data } = await api.post(`/admin/tenants/${id}/users/${u.id}/access-link`);
      const link = `${window.location.origin}/accesso-diretto?token=${encodeURIComponent(data.token)}`;
      const info = { link, password };
      await navigator.clipboard.writeText(fullAccessMessage(u, info)).catch(() => {});
      setFullAccess((current) => ({ ...current, [u.id]: info }));
      toast.success("Link e nuova password generati e copiati");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingFull(null); }
  };
  const add = async () => {
    if (!f.name) return;
    const body = { ...f };
    if (f.role === "addetto") { delete body.password; body.signature_data = signature; } else { delete body.pin; }
    try { await api.post(`/admin/tenants/${id}/users`, body); toast.success("Utente creato"); setF({ name: "", email: "", role: group === "workers" ? "addetto" : "titolare", password: "", pin: "", site_ids: [], haccp_self_control_manager: false, is_employer: false }); resetNewUserSignature(); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleSite = (sid) => setF((p) => ({ ...p, site_ids: p.site_ids.includes(sid) ? p.site_ids.filter((x) => x !== sid) : [...p.site_ids, sid] }));
  const removeDevice = async (userId, deviceId) => {
    if (!window.confirm("Togliere questo dispositivo dall’elenco? Le sessioni aperte dell’addetto verranno chiuse; potrà comunque rientrare con email aziendale e PIN.")) return;
    try {
      await api.delete(`/admin/tenants/${id}/users/${userId}/devices/${encodeURIComponent(deviceId)}`);
      toast.success("Dispositivo tolto e sessioni chiuse"); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const closePinReset = () => { setPinResetUser(null); setNewPin(""); setConfirmPin(""); };
  const resetPin = async () => {
    if (!/^\d{6,12}$/.test(newPin)) { toast.error("Il PIN deve contenere da 6 a 12 cifre"); return; }
    if (newPin !== confirmPin) { toast.error("I due PIN non coincidono"); return; }
    if (!window.confirm(`Reimpostare il PIN di ${pinResetUser.name}? Le sessioni attive saranno revocate.`)) return;
    setResettingPin(true);
    try {
      await api.post(`/admin/tenants/${id}/users/${pinResetUser.id}/reset-pin`, { pin: newPin });
      toast.success("PIN reimpostato e sessioni precedenti revocate");
      closePinReset(); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResettingPin(false); }
  };
  const closePasswordReset = () => { setPasswordResetUser(null); setNewPassword(""); setConfirmPassword(""); setShowPassword(false); };
  const resetPassword = async () => {
    if (newPassword.length < 12) { toast.error("La password deve contenere almeno 12 caratteri"); return; }
    if (newPassword !== confirmPassword) { toast.error("Le due password non coincidono"); return; }
    if (!window.confirm(`Reimpostare la password di ${passwordResetUser.name}? Le sessioni attive saranno revocate.`)) return;
    setResettingPassword(true);
    try {
      const u = passwordResetUser;
      await api.post(`/admin/tenants/${id}/users/${u.id}/reset-password`, { password: newPassword });
      const { data } = await api.post(`/admin/tenants/${id}/users/${u.id}/access-link`);
      const link = `${window.location.origin}/accesso-diretto?token=${encodeURIComponent(data.token)}`;
      const info = { link, password: newPassword };
      await navigator.clipboard.writeText(fullAccessMessage(u, info)).catch(() => {});
      setFullAccess((current) => ({ ...current, [u.id]: info }));
      toast.success("Password reimpostata: messaggio con email, password e link già copiato");
      closePasswordReset(); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setResettingPassword(false); }
  };
  return (
    <div className="space-y-4" data-testid="utenti-tab">
    <UserSectionTabs value={group} onChange={changeGroup} testid="td-user-tabs"
      ownersCount={activeUsers.filter((u) => u.role !== "addetto").length}
      workersCount={activeUsers.filter((u) => u.role === "addetto").length} />
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="p-6">
        <h3 className="font-heading font-semibold mb-4">{group === "workers" ? "Addetti all’autocontrollo" : "Titolare e responsabili"}</h3>
        <div className="space-y-2">
          {activeUsers.filter(inGroup).length === 0 && <p className="rounded-lg border border-dashed border-hairline p-3 text-sm text-slate-500">{group === "workers" ? "Nessun addetto inserito." : "Nessun titolare o responsabile inserito."}</p>}
          {activeUsers.filter(inGroup).map((u) => (
            <div key={u.id} className="p-3 rounded-lg border border-hairline" data-testid={`user-${u.id}`}>
              <AdminRecordEditor record={u} endpoint={`/admin/tenants/${id}/users/${u.id}`} fields={userFields(d)} reload={reload} deleteMessage={`Eliminare l’accesso di ${u.name}? Le sessioni saranno revocate; firme e registrazioni storiche saranno conservate.`}><div className="flex justify-between gap-3">
                <div>
                  <div className="text-navy font-medium">{u.name}</div><div className="text-xs text-slate-400">{u.role === "addetto" ? `${companyAccessEmail(d.tenant.contacts?.email, d.users.filter((x) => x.active !== false)) || "email aziendale"} · PIN personale` : u.email}</div>
                  {(u.haccp_self_control_manager || u.is_employer) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {u.haccp_self_control_manager && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium" data-testid={`badge-haccp-manager-${u.id}`}>Responsabile autocontrollo</span>}
                      {u.is_employer && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium" data-testid={`badge-employer-${u.id}`}>Datore di lavoro</span>}
                    </div>
                  )}
                </div>
                <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600 self-center">{d.role_labels?.[u.role] || u.role}</span>
              </div></AdminRecordEditor>
              <div className="mt-3 pt-3 border-t border-hairline space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><PenTool className="w-3.5 h-3.5" /> Firma depositata</div>
                {u.signature?.data ? (
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid={`signature-present-${u.id}`}><CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> Depositata il {formatItalianDateTime(u.signature.deposited_at)}</div>
                ) : (
                  <div className="text-xs text-slate-400">Nessuna firma depositata</div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <input type="file" accept="image/jpeg,image/png" className="hidden" id={`sig-upload-${u.id}`}
                    onChange={(e) => { uploadSignature(u.id, e.target.files?.[0]); e.target.value = ""; }} />
                  <label htmlFor={`sig-upload-${u.id}`} className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-hairline px-3 py-1.5 text-xs font-medium cursor-pointer hover:bg-slate-50 ${signatureBusy === u.id ? "opacity-50 pointer-events-none" : ""}`} data-testid={`signature-upload-${u.id}`}>
                    <Upload className="w-3.5 h-3.5" /> Carica firma
                  </label>
                  <div className="inline-flex items-center gap-1.5">
                    <Button variant="outline" className="text-xs px-3 py-1.5 h-auto min-h-[36px]" disabled={!hasClientSignature} loading={signatureBusy === u.id} onClick={() => importSignatureFromDocs(u.id)} data-testid={`signature-import-${u.id}`}>
                      <Download className="w-3.5 h-3.5" /> Da documenti ricevuti
                    </Button>
                    {!hasClientSignature && <FieldHelp text="Nessuna firma ancora ricevuta tramite il modulo Documenti azienda per questa azienda: il pulsante si attiva appena il cliente ne carica una." />}
                  </div>
                </div>
              </div>
              {u.role === "addetto" && (
                <div className="mt-3 pt-3 border-t border-hairline space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Dispositivi usati per il PIN</div>
                  <Button variant="outline" className="w-full" onClick={() => { setPinResetUser(u); setNewPin(""); setConfirmPin(""); }} data-testid={`reset-pin-${u.id}`}><KeyRound className="w-4 h-4" /> Reimposta PIN</Button>
                  {(u.device_ids || []).map((deviceId) => (
                    <div key={`approved-${deviceId}`} className="flex items-center justify-between gap-2 rounded bg-emerald-50 p-2 text-xs">
                      <div className="min-w-0"><div className="font-medium text-emerald-800">Dispositivo usato</div><div className="truncate text-emerald-700" title={deviceId}>{deviceId}</div></div>
                      <button onClick={() => removeDevice(u.id, deviceId)} className="p-2 rounded bg-white text-red-600 border border-red-200" title="Togli dall’elenco e chiudi le sessioni aperte" data-testid={`revoke-device-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {(u.device_ids || []).length === 0 && <div className="text-xs text-slate-400">Nessun accesso ancora effettuato.</div>}
                  <p className="text-xs text-slate-400">Non serve autorizzare nulla: quando l’addetto entra da un dispositivo nuovo ricevi una notifica.</p>
                </div>
              )}
              {u.role !== "addetto" && (
                <div className="mt-3 border-t border-hairline pt-3 space-y-2">
                  {u.password_changed_at && (
                    <p className="text-xs text-slate-500" data-testid={`password-changed-${u.id}`}>
                      Password cambiata il {formatItalianDateTime(u.password_changed_at)}
                      {u.password_changed_by === "admin" ? " (da te)" : " (dal cliente)"}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <div className="flex flex-1 items-center gap-1.5 min-w-[150px]">
                      <Button variant="outline" className="flex-1" loading={impersonating === u.id} onClick={() => impersonate(u)} data-testid={`impersonate-${u.id}`}><LogIn className="w-4 h-4" /> Entra come {u.name}</Button>
                      <FieldHelp text="Entri subito nell'area di questo utente con la tua autorità di consulente, senza vedere né usare la sua password. Riusabile in ogni momento (sessione di 30 minuti); un banner in alto ti riporta al pannello admin con un click, senza rifare login." />
                    </div>
                    <div className="flex flex-1 items-center gap-1.5 min-w-[150px]">
                      <Button variant="outline" className="flex-1" onClick={() => { setPasswordResetUser(u); setNewPassword(""); setConfirmPassword(""); setShowPassword(false); }} data-testid={`reset-password-${u.id}`}><KeyRound className="w-4 h-4" /> Reimposta password</Button>
                      <FieldHelp text="Scegli tu stesso una nuova password per l'utente (utile se vuoi dettarne una semplice da ricordare). Le sessioni già aperte con la password precedente vengono chiuse subito." />
                    </div>
                    <div className="flex flex-1 items-center gap-1.5 min-w-[150px]">
                      <Button variant="outline" className="flex-1" loading={creatingLink === u.id} onClick={() => createAccessLink(u)} data-testid={`access-link-${u.id}`}><Link2 className="w-4 h-4" /> Solo link</Button>
                      <FieldHelp text="Genera un nuovo link di accesso rapido: un click e il cliente entra, senza bisogno di password. È monouso e scade dopo 30 minuti — se il cliente non fa in tempo, premi di nuovo questo pulsante per crearne uno nuovo. Non tocca la password esistente." />
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button variant="success" className="flex-1" loading={sendingFull === u.id} onClick={() => sendFullAccess(u)} data-testid={`full-access-${u.id}`}><Send className="w-4 h-4" /> Invia accesso completo (link + password)</Button>
                    <FieldHelp text="La soluzione più rapida quando il cliente chiama perché non riesce a entrare: crea insieme un link nuovo E una password nuova leggibile, già pronti in un unico messaggio da copiare o da inviare su WhatsApp con un click. Attenzione: la password precedente smette subito di funzionare e le sessioni aperte vengono chiuse." />
                  </div>
                  {accessLinks[u.id] && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 space-y-2" data-testid={`access-link-box-${u.id}`}>
                      <div><strong>Link copiato.</strong> È monouso e scade dopo 30 minuti: se il cliente non fa in tempo, premi di nuovo “Solo link” per generarne uno nuovo.</div>
                      <div className="flex items-center gap-2">
                        <input readOnly value={accessLinks[u.id]} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded border border-amber-200 bg-white px-2 py-1.5 text-[11px] text-slate-600" />
                        <button type="button" onClick={() => { navigator.clipboard.writeText(accessLinks[u.id]); toast.success("Link ricopiato"); }} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg hover:bg-amber-100" aria-label="Copia link"><Copy className="w-4 h-4" /></button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`https://wa.me/${waNumber(d.tenant.contacts?.whatsapp || d.tenant.contacts?.phone)}?text=${encodeURIComponent(`Ciao ${u.name}, ecco il link per accedere a DF HACCP (valido 30 minuti, un solo utilizzo): ${accessLinks[u.id]}`)}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-white hover:bg-emerald-700"
                        ><MessageCircle className="w-3.5 h-3.5" /> Apri WhatsApp</a>
                        <a
                          href={`mailto:${encodeURIComponent(u.email)}?subject=${encodeURIComponent("Accesso a DF HACCP")}&body=${encodeURIComponent(`Ciao ${u.name}, ecco il link per accedere a DF HACCP (valido 30 minuti, un solo utilizzo): ${accessLinks[u.id]}`)}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-amber-800 hover:bg-amber-100"
                        ><Mail className="w-3.5 h-3.5" /> Invia via email</a>
                      </div>
                    </div>
                  )}
                  {fullAccess[u.id] && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 space-y-2" data-testid={`full-access-box-${u.id}`}>
                      <div><strong>Nuova password creata e link generato.</strong> Restano visibili qui finché non esci da questa scheda cliente o non li rigeneri. Il messaggio completo (email + password + link) è già copiato: incollalo dove preferisci, oppure usa i pulsanti qui sotto.</div>
                      <div className="rounded border border-emerald-200 bg-white p-3 space-y-1.5">
                        <div>Email: <strong className="select-all">{u.email}</strong></div>
                        <div className="flex items-center gap-2">
                          <span>Password:</span>
                          <strong data-testid={`full-access-password-${u.id}`} className="select-all font-mono text-base tracking-wider">{fullAccess[u.id].password}</strong>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => { navigator.clipboard.writeText(fullAccessMessage(u, fullAccess[u.id])); toast.success("Messaggio ricopiato"); }} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 hover:bg-emerald-100"><Copy className="w-3.5 h-3.5" /> Ricopia messaggio</button>
                        <a
                          href={`https://wa.me/${waNumber(d.tenant.contacts?.whatsapp || d.tenant.contacts?.phone)}?text=${encodeURIComponent(fullAccessMessage(u, fullAccess[u.id]))}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-white hover:bg-emerald-700"
                        ><MessageCircle className="w-3.5 h-3.5" /> Apri WhatsApp</a>
                        <a
                          href={`mailto:${encodeURIComponent(u.email)}?subject=${encodeURIComponent("Accesso a DF HACCP")}&body=${encodeURIComponent(fullAccessMessage(u, fullAccess[u.id]))}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 hover:bg-emerald-100"
                        ><Mail className="w-3.5 h-3.5" /> Invia via email</a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-6 space-y-2">
        <h3 className="font-heading font-semibold mb-2">{group === "workers" ? "Nuovo addetto" : "Nuovo titolare o responsabile"}</h3>
        <Input placeholder="Nome e cognome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" />
        {group === "owners" && <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="user-role"><option value="titolare">Titolare</option><option value="responsabile">Responsabile</option><option value="ispettore">Ispettore (sola lettura)</option></Select>}
        {f.role === "addetto"
          ? <CompanyAccessEmail email={companyAccessEmail(d.tenant.contacts?.email, d.users.filter((u) => u.active !== false))} testid="user-company-email" />
          : <div className="flex gap-2">
              <Input type="email" autoComplete="off" placeholder="Email di accesso (puoi aggiungerla dopo)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="min-w-0 flex-1" data-testid="user-email" />
              <CopyCompanyEmailButton companyEmail={d.tenant.contacts?.email} onCopy={(email) => setF({ ...f, email })} testid="user-email-copy" />
            </div>}
        {f.role === "addetto"
          ? <Input placeholder="PIN (min 6 cifre)" value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value })} data-testid="user-pin" />
          : <PasswordInput autoComplete="new-password" placeholder="Password (min 8)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" />}
        {f.role === "addetto" && (
          <div className="pt-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <input type="file" accept="image/jpeg,image/png" className="hidden" id="new-user-signature-upload"
                onChange={(e) => { uploadNewUserSignatureFile(e.target.files?.[0]); e.target.value = ""; }} />
              <label htmlFor="new-user-signature-upload" className="inline-flex min-h-[36px] cursor-pointer items-center gap-1.5 rounded-lg border border-hairline px-3 py-1.5 text-xs font-medium hover:bg-slate-50" data-testid="new-user-signature-upload">
                <Upload className="w-3.5 h-3.5" /> Carica firma
              </label>
              <Button variant="outline" className="text-xs px-3 py-1.5 h-auto min-h-[36px]" disabled={!hasClientSignature} loading={newUserSignatureBusy} onClick={importNewUserSignatureFromDocs} data-testid="new-user-signature-import">
                <Download className="w-3.5 h-3.5" /> Da documenti ricevuti
              </Button>
              {!hasClientSignature && <FieldHelp text="Nessuna firma ancora ricevuta tramite il modulo Documenti azienda per questa azienda." />}
            </div>
            {signatureMode === "preview" && signature ? (
              <div className="rounded-lg border border-hairline p-2 text-center" data-testid="new-user-signature-preview">
                <img src={signature} alt="Anteprima firma" className="mx-auto max-h-32" />
                <button type="button" onClick={resetNewUserSignature} className="mt-1.5 text-xs text-slate-400 underline hover:text-navy">Rimuovi e disegna sul tablet</button>
              </div>
            ) : (
              <SignaturePad onChange={setSignature} />
            )}
          </div>
        )}
        <div className="text-xs uppercase tracking-wider text-slate-400 pt-1">Sedi assegnate</div>
        <div className="flex flex-wrap gap-2">
          {d.sites.map((s) => <button key={s.id} type="button" onClick={() => toggleSite(s.id)} className={`text-xs px-3 py-1.5 rounded-full border min-h-[36px] ${f.site_ids.includes(s.id) ? "bg-navy text-white border-navy" : "border-hairline text-slate-500"}`}>{s.name}</button>)}
        </div>
        <div className="pt-1 space-y-1.5">
          <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.haccp_self_control_manager} onChange={(e) => setF({ ...f, haccp_self_control_manager: e.target.checked, is_employer: e.target.checked ? f.is_employer : false })} className="h-5 w-5 accent-navy" data-testid="user-haccp-manager" /> Responsabile dell'autocontrollo</label>
          {f.haccp_self_control_manager && (
            <label className="ml-6 flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={f.is_employer} onChange={(e) => setF({ ...f, is_employer: e.target.checked })} className="h-5 w-5 accent-navy" data-testid="user-is-employer" /> Coincide con il Datore di lavoro</label>
          )}
        </div>
        <Button variant="success" className="w-full" onClick={add} data-testid="add-user"><Plus className="w-4 h-4" /> Crea utente</Button>
      </Card>
      {pinResetUser && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true" aria-labelledby="reset-pin-title">
          <button className="absolute inset-0 bg-navy/50" onClick={closePinReset} aria-label="Chiudi finestra reimposta PIN" />
          <Card className="relative w-full max-w-md p-6">
            <div className="flex items-start justify-between gap-3">
              <div><h3 id="reset-pin-title" className="font-heading text-xl font-semibold">Reimposta PIN</h3><p className="mt-1 text-sm text-slate-500">{pinResetUser.name} · {pinResetUser.email}</p></div>
              <button onClick={closePinReset} className="grid min-h-[44px] min-w-[44px] place-items-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Chiudi"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <Field label="Nuovo PIN" hint="Da 6 a 12 cifre, diverso da quello degli altri addetti dell’azienda; non sarà visualizzabile dopo il salvataggio."><Input type="password" inputMode="numeric" autoComplete="new-password" maxLength={12} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} data-testid="new-pin" /></Field>
              <Field label="Ripeti nuovo PIN"><Input type="password" inputMode="numeric" autoComplete="new-password" maxLength={12} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))} data-testid="confirm-pin" /></Field>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Button variant="ghost" onClick={closePinReset}>Annulla</Button><Button variant="primary" loading={resettingPin} onClick={resetPin} data-testid="confirm-reset-pin"><KeyRound className="w-4 h-4" /> Salva nuovo PIN</Button></div>
            </div>
          </Card>
        </div>
      )}
      {passwordResetUser && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true" aria-labelledby="reset-password-title">
          <button className="absolute inset-0 bg-navy/50" onClick={closePasswordReset} aria-label="Chiudi finestra reimposta password" />
          <Card className="relative w-full max-w-md p-6">
            <div className="flex items-start justify-between gap-3">
              <div><h3 id="reset-password-title" className="font-heading text-xl font-semibold">Reimposta password</h3><p className="mt-1 text-sm text-slate-500">{passwordResetUser.name} · {passwordResetUser.email}</p></div>
              <button onClick={closePasswordReset} className="grid min-h-[44px] min-w-[44px] place-items-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Chiudi"><X className="w-5 h-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <Field label="Nuova password" hint="Almeno 12 caratteri; le sessioni già aperte verranno revocate.">
                <div className="relative"><input type={showPassword ? "text" : "password"} autoComplete="new-password" className={`${inputCls} pr-12`} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password" /><button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-1.5 top-1/2 grid min-h-[40px] min-w-[40px] -translate-y-1/2 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label={showPassword ? "Nascondi password" : "Mostra password"}>{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div>
              </Field>
              <Field label="Ripeti nuova password"><Input type={showPassword ? "text" : "password"} autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="confirm-password" /></Field>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Button variant="ghost" onClick={closePasswordReset}>Annulla</Button><Button variant="primary" loading={resettingPassword} onClick={resetPassword} data-testid="confirm-reset-password"><KeyRound className="w-4 h-4" /> Salva nuova password</Button></div>
            </div>
          </Card>
        </div>
      )}
    </div>
    </div>
  );
}

function ModuliTab({ id, d, reload }) {
  const endpoint = `/admin/tenants/${id}`;
  const [draft, setDraft] = useState(() => Object.fromEntries(d.modules_catalog.map((m) => [m.key, m.active])));
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDraft(Object.fromEntries(d.modules_catalog.map((m) => [m.key, m.active])));
  }, [d.modules_catalog]);

  const dirty = d.modules_catalog.some((m) => !!draft[m.key] !== !!m.active);
  const toggle = (key) => setDraft((current) => ({ ...current, [key]: !current[key] }));
  const setManyValues = (keys, value) => setDraft((current) => ({ ...current, ...Object.fromEntries(keys.map((key) => [key, value])) }));
  const discard = () => setDraft(Object.fromEntries(d.modules_catalog.map((m) => [m.key, m.active])));
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(endpoint, { modules: draft, reason: "Modifica moduli da pannello Servizio & moduli" });
      toast.success("Moduli aggiornati");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return <div className="space-y-4" data-testid="moduli-tab">
    <Card className="p-6"><h3 className="mb-4 font-heading font-semibold">Servizio di consulenza</h3>
      <AdminRecordEditor record={{ name: "Personalizzazioni del servizio", plan: d.tenant.plan }} endpoint={endpoint} reload={reload}
        fields={[{ key: "plan", label: "Livello di consulenza", options: [["BASE", "Base"], ["PREMIUM", "Premium"], ["SUPER_PREMIUM", "Super Premium"], ["CUSTOM", "Personalizzato"]] }]}
        prepare={(draft) => ({ plan: draft.plan, modules: d.tenant.modules })}
        onRemove={() => api.patch(endpoint, { plan: d.tenant.plan })}
        deleteMessage="Eliminare le personalizzazioni dei moduli e ripristinare quelli previsti dal piano attuale? I dati registrati restano conservati.">
        <Info label="Livello di consulenza" value={d.tenant.plan} />
      </AdminRecordEditor>
    </Card>

    {dirty && (
      <div className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm" data-testid="moduli-unsaved-bar">
        <span>Hai modifiche non salvate ai moduli.</span>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={discard}>Annulla modifiche</Button>
          <Button variant="success" loading={saving} onClick={save} data-testid="save-modules"><Check className="w-4 h-4" /> Salva modifiche</Button>
        </div>
      </div>
    )}

    <ModuleSelector
      modules={d.modules_catalog.map((m) => ({ ...m, badge: m.plan_label }))}
      value={draft}
      onToggle={toggle}
      onSetMany={setManyValues}
    />
    <ClientDailyReportCard id={id} active={Boolean(d.tenant.modules?.notifiche_auto)} />
    <ComplianceAlertHistoryCard id={id} />
  </div>;
}

// Report serale registri al titolare (modulo "notifiche_auto"): anteprima con i dati
// di oggi fino a questo momento e invio manuale di prova, oltre all'invio automatico delle 20:00.
function ClientDailyReportCard({ id, active }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [historyKey, setHistoryKey] = useState(0);
  const loadPreview = async () => {
    setBusy("preview");
    try { const { data } = await api.get(`/admin/tenants/${id}/client-daily-report/preview`); setPreview(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Anteprima non disponibile"); }
    finally { setBusy(""); }
  };
  const sendNow = async () => {
    if (!window.confirm("Inviare adesso al titolare il report dei registri di oggi (notifica nell’app, push ed email)? L’invio automatico delle 20:00 resta invariato.")) return;
    setBusy("send");
    try {
      const { data } = await api.post(`/admin/tenants/${id}/client-daily-report/send-now`);
      if (!data.recipients) toast.error("Nessun titolare attivo a cui inviare il report");
      else {
        const emailsOk = data.results.filter((r) => r.email_status === "sent").length;
        toast.success(`Report inviato a ${data.recipients} titolare/i · email consegnate al server di posta: ${emailsOk}`);
        setHistoryKey((k) => k + 1);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setBusy(""); }
  };
  return <Card className="space-y-4 p-5" data-testid="client-daily-report-card">
    <div>
      <h3 className="font-heading font-semibold">Report giornaliero al titolare</h3>
      <p className={`mt-1 text-sm ${active ? "text-emerald-700" : "text-amber-700"}`}>{active
        ? "Attivo: ogni sera alle 20:00 il titolare riceve nell’app e via email il riepilogo dei registri di sedi, reparti e addetti."
        : "Non attivo: per inviarlo ogni sera alle 20:00 attiva il modulo “Notifiche automatiche giornaliere” (Assistenza) e salva."}</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" loading={busy === "preview"} onClick={loadPreview} data-testid="client-daily-report-preview"><Eye className="h-4 w-4" /> Anteprima di oggi</Button>
      <Button variant="success" loading={busy === "send"} onClick={sendNow} data-testid="client-daily-report-send"><Send className="h-4 w-4" /> Invia ora al titolare</Button>
    </div>
    {preview && <div className="rounded-lg border border-hairline bg-slate-50 p-4">
      <p className="mb-3 text-xs text-slate-500">Anteprima con i dati registrati finora: nulla è stato inviato.</p>
      <DailyReportText body={preview.body} />
    </div>}
    <ClientDailyReportHistory id={id} refreshKey={historyKey} />
  </Card>;
}

// Avvisi ⚡ inviati dal "Report serale" per registrazioni mancanti: quante volte
// questo mese, con che testo e se il cliente ne ha preso visione.
function ComplianceAlertHistoryCard({ id }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/compliance-alerts/tenants/${id}`)
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [id]);
  return <Card className="space-y-3 p-5" data-testid="compliance-alert-history">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="inline-flex items-center gap-2 font-heading font-semibold"><Zap className="h-4 w-4 text-gold" /> Avvisi per registrazioni mancanti</h3>
      {data && <span className={`text-sm font-semibold ${data.this_month ? "text-amber-700" : "text-emerald-700"}`}>{data.this_month} questo mese</span>}
    </div>
    <p className="text-xs text-slate-500">Si inviano dal Report serale. Il titolare li riceve nell’app, come push e via email.</p>
    {error && <p className="text-sm text-red-700">Storico non disponibile. Riprova più tardi.</p>}
    {!error && !data && <Spinner />}
    {data?.items.length === 0 && <p className="text-sm text-slate-500">Nessun avviso inviato finora.</p>}
    {data?.items.length > 0 && <ul className="space-y-2">
      {data.items.map((item) => <li key={item.id} className="rounded-lg border border-hairline p-3 text-sm">
        <p className="font-medium">Registri del {item.day_label} · inviato {formatItalianDateTime(item.created_at)}</p>
        <p className="mt-1 break-words text-slate-700">{item.intro}</p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {(item.recipients || []).length === 0 && <span className="text-amber-700">Nessun titolare attivo: solo banner nell’app</span>}
          {(item.recipients || []).map((r) => <span key={r.user_id} className="flex flex-wrap gap-x-3"><span className="text-slate-500">A: {r.name || r.email}</span><DeliveryStatus label="Push" status={r.push_status} /><DeliveryStatus label="Email" status={r.email_status} /></span>)}
          <span><span className="text-slate-500">Presa visione:</span> <span className={`font-medium ${item.acknowledged_at ? "text-emerald-700" : "text-amber-700"}`}>{item.acknowledged_at ? `Sì, ${formatItalianDateTime(item.acknowledged_at)}${item.acknowledged_by_name ? ` (${item.acknowledged_by_name})` : ""}` : "Non ancora"}</span></span>
        </div>
      </li>)}
    </ul>}
  </Card>;
}

const DELIVERY_STATUS = {
  sent: ["Inviata", "text-emerald-700"],
  failed: ["Non riuscita", "text-red-700"],
  not_configured: ["Email non configurata", "text-amber-700"],
  not_subscribed: ["Notifiche non attivate sul telefono", "text-amber-700"],
  unknown: ["Non registrato", "text-slate-500"],
};
const TRIGGER_LABEL = { auto: "Automatico 20:00", manual: "Manuale (Invia ora)", unknown: "Invio precedente allo storico" };

function DeliveryStatus({ label, status }) {
  const [text, cls] = DELIVERY_STATUS[status] || [status || "—", "text-slate-500"];
  return <span><span className="text-slate-500">{label}:</span> <span className={`font-medium ${cls}`}>{text}</span></span>;
}

// Storico invii del report al titolare: cosa è stato inviato, a chi e con quale esito.
function ClientDailyReportHistory({ id, refreshKey }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    let alive = true;
    setError(false);
    api.get(`/admin/tenants/${id}/client-daily-report/history`)
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [id, refreshKey]);
  return <div className="space-y-3 border-t border-hairline pt-4" data-testid="client-daily-report-history">
    <div>
      <h4 className="font-heading text-sm font-semibold">Storico invii</h4>
      <p className="text-xs text-slate-500">Ultimi report inviati: destinatario, esito di notifica, push ed email, e se il titolare li ha letti.</p>
    </div>
    {error && <p className="text-sm text-red-700">Storico non disponibile. Riprova più tardi.</p>}
    {!error && items === null && <Spinner />}
    {!error && items?.length === 0 && <p className="text-sm text-slate-500">Nessun report inviato finora.</p>}
    {items?.length > 0 && <ul className="space-y-2">
      {items.map((item) => <li key={item.id} className="rounded-lg border border-hairline p-3 text-sm" data-testid="client-daily-report-history-item">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium">{formatItalianDateTime(item.created_at)} · <span className="text-slate-600">{TRIGGER_LABEL[item.trigger] || item.trigger}</span></p>
            <p className="break-words text-slate-700">A: {item.recipient_name}{item.recipient_email ? ` (${item.recipient_email})` : ""}</p>
          </div>
          <Button variant="outline" onClick={() => setOpenId(openId === item.id ? null : item.id)} data-testid="client-daily-report-history-toggle">
            {openId === item.id ? <><EyeOff className="h-4 w-4" /> Chiudi testo</> : <><Eye className="h-4 w-4" /> Vedi testo</>}
          </Button>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <span><span className="text-slate-500">Notifica nell’app:</span> <span className="font-medium text-emerald-700">Inviata</span></span>
          <DeliveryStatus label="Push" status={item.push_status} />
          <DeliveryStatus label="Email" status={item.email_status} />
          <span><span className="text-slate-500">Letta dal titolare:</span> <span className={`font-medium ${item.read ? "text-emerald-700" : "text-amber-700"}`}>{item.read ? (item.read_at ? `Sì, ${formatItalianDateTime(item.read_at)}` : "Sì") : "Non ancora"}</span></span>
        </div>
        {openId === item.id && <div className="mt-3 rounded-lg border border-hairline bg-slate-50 p-4">
          {item.body ? <DailyReportText body={item.body} /> : <p className="text-slate-500">Testo non disponibile.</p>}
        </div>}
      </li>)}
    </ul>}
  </div>;
}

function IntakeTab({ id, t }) {
  const [data, setData] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const waNumber = (raw) => (raw || "").replace(/\D/g, "");
  const [selected, setSelected] = useState({ suppliers: {}, ingredients: {}, recipes: {} });
  const [busy, setBusy] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [fixingVat, setFixingVat] = useState({});
  const load = useCallback(() => api.get(`/admin/tenants/${id}/intake`).then(({ data }) => {
    setData(data);
    setSelected({
      suppliers: Object.fromEntries((data.submission?.suppliers || []).map((s) => [s.temp_id, true])),
      ingredients: Object.fromEntries((data.submission?.ingredients || []).map((i) => [i.temp_id, true])),
      recipes: Object.fromEntries((data.submission?.recipes || []).map((r) => [r.temp_id, true])),
    });
    if (data.link?.status !== "open") {
      setLinkUrl("");
    }
  }).catch(() => toast.error("Errore caricamento")), [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const generateLink = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/tenants/${id}/intake-link`);
      const url = `${window.location.origin}/intake/${res.token}`;
      setLinkUrl(url);
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti: valido finché non lo rigeneri o lo blocchi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const blockLink = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${id}/intake-link/block`);
      setLinkUrl("");
      toast.success("Link bloccato: il cliente non può più accedervi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = (group, itemId) => setSelected((cur) => ({ ...cur, [group]: { ...cur[group], [itemId]: !cur[group][itemId] } }));

  const runImport = async () => {
    setBusy(true);
    try {
      const body = {
        supplier_temp_ids: Object.entries(selected.suppliers).filter(([, v]) => v).map(([k]) => k),
        ingredient_temp_ids: Object.entries(selected.ingredients).filter(([, v]) => v).map(([k]) => k),
        recipe_temp_ids: Object.entries(selected.recipes).filter(([, v]) => v).map(([k]) => k),
      };
      const { data: res } = await api.post(`/admin/tenants/${id}/intake/import`, body);
      toast.success(`Importati: ${res.suppliers.created} fornitori, ${res.ingredients.created} ingredienti, ${res.recipes.created} ricette`);
      setImportErrors(res.errors || []);
      if (res.errors?.length > 0) toast.error(`${res.errors.length} elementi non importati: dettagli sotto ogni riga`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fixSupplierVat = async (tempId, vat) => {
    setFixingVat((cur) => ({ ...cur, [tempId]: true }));
    try {
      await api.patch(`/admin/tenants/${id}/intake/suppliers/${tempId}`, { vat });
      toast.success("P.IVA corretta: riprova a importare questo fornitore");
      setImportErrors((cur) => cur.filter((e) => e.temp_id !== tempId));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setFixingVat((cur) => ({ ...cur, [tempId]: false })); }
  };

  if (!data) return <Spinner />;
  const submission = data.submission || { suppliers: [], ingredients: [], recipes: [] };
  const hasData = submission.suppliers.length + submission.ingredients.length + submission.recipes.length > 0;
  const selectedCount = [selected.suppliers, selected.ingredients, selected.recipes]
    .reduce((sum, group) => sum + Object.values(group).filter(Boolean).length, 0);

  return (
    <div className="space-y-4" data-testid="intake-tab">
      <Card className="p-6 space-y-3">
        <div className="rounded-lg bg-indigo-50 border border-indigo-200 px-4 py-3">
          <p className="text-sm font-semibold text-indigo-900">📋 Link per far creare le RICETTE al cliente</p>
          <p className="text-xs text-indigo-800 mt-0.5">Il cliente usa questo link per inserire da solo le sue ricette (indicando fornitori e ingredienti necessari a costruirle). Tu poi importi e completi ogni ricetta prima di approvarla.</p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading font-semibold">Link modulo ricette per il cliente</h3>
            <p className="text-sm text-slate-500 mt-1">
              {!data.link
                ? "Nessun link generato finora."
                : data.link.status === "blocked"
                  ? "Link disattivato: il cliente non può più accedervi. Genera un nuovo link per riattivare l'accesso."
                  : "Link attivo: il cliente può compilarlo e tornarci sopra quando vuole, anche in momenti diversi."}
              {data.link?.imported_at && <span className="block text-xs text-slate-400 mt-0.5">Ultimo import: {new Date(data.link.imported_at).toLocaleString("it-IT")}</span>}
            </p>
          </div>
          <div className="flex gap-2">
            {data.link?.status === "open" && (
              <Button variant="danger" loading={busy} onClick={blockLink} data-testid="intake-block-link"><Ban className="w-4 h-4" /> Blocca link</Button>
            )}
            <Button variant={data.link ? "outline" : "success"} loading={busy} onClick={generateLink} data-testid="intake-generate-link">
              {data.link ? <><RefreshCw className="w-4 h-4" /> {data.link.status === "blocked" ? "Genera nuovo link" : "Rigenera link"}</> : <><Link2 className="w-4 h-4" /> Genera link</>}
            </Button>
          </div>
        </div>
        {linkUrl && (
          <>
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              <span className="truncate flex-1 font-mono">{linkUrl}</span>
              <button type="button" onClick={() => { navigator.clipboard.writeText(linkUrl); toast.success("Link ricopiato"); }} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg hover:bg-emerald-100" aria-label="Copia link"><Copy className="w-4 h-4" /></button>
            </div>
            <a href={`https://wa.me/${waNumber(t?.contacts?.whatsapp || t?.contacts?.phone)}?text=${encodeURIComponent(`Ciao, per inserire le tue ricette (e fornitori/materie prime se ti servono) usa questo link: ${linkUrl}`)}`}
              target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-1.5 text-white hover:brightness-95" data-testid="intake-whatsapp">
              <MessageCircle className="w-4 h-4" /> Condividi su WhatsApp
            </a>
          </>
        )}
      </Card>

      {hasData && (
        <Card className="p-6 space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-heading font-semibold">Dati inviati dal cliente — controlla e importa</h3>
            <Button variant="success" loading={busy} disabled={selectedCount === 0} onClick={runImport} data-testid="intake-import">Importa {selectedCount} elementi</Button>
          </div>
          <IntakeGroup title="Fornitori" items={submission.suppliers} selected={selected.suppliers} onToggle={(itemId) => toggle("suppliers", itemId)}
            renderDetail={(s) => [s.vat, s.email, s.phone].filter(Boolean).join(" · ")}
            errorFor={(itemId) => importErrors.find((e) => e.temp_id === itemId && e.entity === "supplier")}
            onFixVat={fixSupplierVat} fixingVat={fixingVat} />
          <IntakeGroup title="Ingredienti" items={submission.ingredients} selected={selected.ingredients} onToggle={(itemId) => toggle("ingredients", itemId)}
            renderDetail={(i) => (i.allergens || []).join(", ") || "Nessun allergene segnalato"}
            errorFor={(itemId) => importErrors.find((e) => e.temp_id === itemId && e.entity === "ingredient")} />
          <IntakeGroup title="Ricette" items={submission.recipes} selected={selected.recipes} onToggle={(itemId) => toggle("recipes", itemId)}
            renderDetail={(r) => `${(r.ingredient_temp_ids?.length || 0) + (r.existing_ingredient_ids?.length || 0)} ingredienti${r.is_cooked ? " · sottoposta a cottura" : ""}`}
            errorFor={(itemId) => importErrors.find((e) => e.temp_id === itemId && e.entity === "recipe")} />
        </Card>
      )}
    </div>
  );
}

function IntakeGroup({ title, items, selected, onToggle, renderDetail, errorFor, onFixVat, fixingVat }) {
  const [vatDrafts, setVatDrafts] = useState({});
  if (items.length === 0) return null;
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{title} ({items.length})</div>
      <div className="divide-y divide-hairline rounded-lg border border-hairline overflow-hidden">
        {items.map((item) => {
          const error = errorFor?.(item.temp_id);
          const isDuplicateError = error && /già presente|già in uso/i.test(error.detail || "");
          return (
          <div key={item.temp_id} className="p-3">
            <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={Boolean(selected[item.temp_id])} onChange={() => onToggle(item.temp_id)} className="h-5 w-5 rounded accent-emerald-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-navy truncate">{item.name || "—"}</div>
              <div className="text-xs text-slate-400 truncate">{renderDetail(item)}</div>
            </div>
            {item.duplicate
              ? <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full"><AlertTriangle className="w-3 h-3" /> Già presente</span>
              : <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle2 className="w-3 h-3" /> Nuovo</span>}
            </label>
            {error && !isDuplicateError && (
              <div className="mt-2 ml-8 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-800">
                <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Non importato: {error.detail}</div>
                {onFixVat && (
                  <div className="mt-2 flex items-center gap-2">
                    <Input placeholder="P.IVA (11 cifre)" value={vatDrafts[item.temp_id] ?? item.vat ?? ""}
                      onChange={(e) => setVatDrafts((cur) => ({ ...cur, [item.temp_id]: e.target.value }))}
                      className="h-9 flex-1 bg-white text-xs" />
                    <Button variant="outline" className="h-9 shrink-0 px-3 text-xs" loading={fixingVat?.[item.temp_id]}
                      onClick={() => onFixVat(item.temp_id, vatDrafts[item.temp_id] ?? item.vat)}>Correggi</Button>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

function ClientDocsTab({ id, t }) {
  const [data, setData] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [deletingField, setDeletingField] = useState(null);
  const waNumber = (raw) => (raw || "").replace(/\D/g, "");
  const load = useCallback(() => api.get(`/admin/tenants/${id}/client-docs`).then(({ data }) => {
    setData(data);
    if (data.link?.status !== "open") {
      setLinkUrl("");
    }
  }).catch(() => toast.error("Errore caricamento")), [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const deleteDoc = async (field) => {
    if (!window.confirm(`Eliminare definitivamente "${field.label}"? Assicurati di averlo prima scaricato, se ti serve conservarlo altrove.`)) return;
    setDeletingField(field.key);
    try {
      await api.delete(`/admin/tenants/${id}/client-docs/${field.key}`);
      toast.success("Documento eliminato");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDeletingField(null); }
  };

  const generateLink = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/tenants/${id}/client-doc-link`);
      const url = `${window.location.origin}/documenti/${res.token}`;
      setLinkUrl(url);
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti: valido 30 giorni, finché non lo rigeneri o lo blocchi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const blockLink = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${id}/client-doc-link/block`);
      setLinkUrl("");
      toast.success("Link bloccato: il cliente non può più accedervi");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <Spinner />;
  const waHref = (url) => `https://wa.me/${waNumber(t.contacts?.whatsapp || t.contacts?.phone)}?text=${encodeURIComponent(`Ciao, per completare la tua pratica HACCP carica i documenti richiesti a questo link: ${url}`)}`;

  return (
    <div className="space-y-4" data-testid="client-docs-tab">
      <Card className="p-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading font-semibold">Link documenti per il cliente</h3>
            <p className="text-sm text-slate-500 mt-1">
              {!data.link
                ? "Nessun link generato finora."
                : data.link.status === "blocked"
                  ? "Link disattivato: il cliente non può più accedervi. Genera un nuovo link per riattivarlo."
                  : data.link.status === "expired"
                    ? `Link scaduto${data.link.expires_at ? ` il ${formatItalianDateTime(data.link.expires_at)}` : ""}: il cliente non può più accedervi. Genera un nuovo link e invialo di nuovo.`
                    : "Link attivo: il cliente può caricare i documenti quando vuole, anche in più momenti diversi."}
              {data.link?.status === "open" && data.link.expires_at && (
                <span className="block text-xs text-slate-400 mt-0.5" data-testid="client-docs-link-expiry">Valido fino al {formatItalianDateTime(data.link.expires_at)} (30 giorni dalla generazione)</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.link?.status === "open" && (
              <Button variant="danger" loading={busy} onClick={blockLink} data-testid="client-docs-block-link"><Ban className="w-4 h-4" /> Blocca link</Button>
            )}
            <Button variant={data.link ? "outline" : "success"} loading={busy} onClick={generateLink} data-testid="client-docs-generate-link">
              {data.link ? <><RefreshCw className="w-4 h-4" /> {data.link.status !== "open" ? "Genera nuovo link" : "Rigenera link"}</> : <><Link2 className="w-4 h-4" /> Genera link</>}
            </Button>
          </div>
        </div>
        {linkUrl && (
          <>
            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              <span className="truncate flex-1 font-mono">{linkUrl}</span>
              <button type="button" onClick={() => { navigator.clipboard.writeText(linkUrl); toast.success("Link ricopiato"); }} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg hover:bg-emerald-100" aria-label="Copia link"><Copy className="w-4 h-4" /></button>
            </div>
            <a href={waHref(linkUrl)} target="_blank" rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-1.5 text-white hover:brightness-95" data-testid="client-docs-whatsapp"><MessageCircle className="w-4 h-4" /> Condividi su WhatsApp</a>
          </>
        )}
      </Card>

      <Card className="p-6 space-y-3">
        <h3 className="font-heading font-semibold">Stato documenti</h3>
        {data.submission && (
          <p className="text-sm text-slate-500">
            Ultimo invio del cliente: {formatItalianDateTime(data.submission.submitted_at)}
            {data.submission.missing_fields?.length > 0 && (
              <span className="text-amber-700"> — mancano ancora {data.submission.missing_fields.length} document{data.submission.missing_fields.length === 1 ? "o" : "i"}</span>
            )}
          </p>
        )}
        <div className="divide-y divide-hairline rounded-lg border border-hairline overflow-hidden">
          {data.fields.map((f) => (
            <div key={f.key} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3" data-testid={`client-doc-status-${f.key}`}>
              <div className="min-w-0">
                <div className="font-medium text-navy flex flex-wrap items-center gap-2">{f.label}{f.required && <span className="text-[10px] uppercase text-red-600 font-semibold">Obbligatoria</span>}</div>
                {f.present ? <div className="truncate text-xs text-slate-400">{f.filename} · {formatItalianDateTime(f.uploaded_at)}</div> : <div className="text-xs text-slate-400">Non caricato</div>}
              </div>
              {f.present ? (
                <div className="flex flex-wrap items-center gap-3 sm:shrink-0">
                  {/* Link semplice nella stessa scheda (mai target="_blank"): qualunque anteprima
                     personalizzata (modale, iframe, blob) si è rivelata inaffidabile su Safari da
                     telefono (pagina bianca, pulsante Chiudi irraggiungibile). Così il browser usa
                     il suo visualizzatore nativo — immagine o PDF — con il normale tasto Indietro
                     per tornare qui: l'unico meccanismo che non possiamo rompere. */}
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${id}/client-docs/${f.key}/file`} rel="noreferrer" className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-navy underline" data-testid={`client-doc-open-${f.key}`}><Eye className="w-3.5 h-3.5" /> Apri</a>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants/${id}/client-docs/${f.key}/file`} download={f.filename} className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-navy underline" data-testid={`client-doc-download-${f.key}`}><Download className="w-3.5 h-3.5" /> Scarica</a>
                  <button type="button" onClick={() => deleteDoc(f)} disabled={deletingField === f.key} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50" aria-label={`Elimina ${f.label}`} data-testid={`client-doc-delete-${f.key}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ) : (
                <span className={`self-start shrink-0 text-xs px-2 py-1 rounded-full sm:self-auto ${f.required ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-500"}`}>Mancante</span>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function SospensioneTab({ id, t, reload }) {
  const save = (draft) => api.post(`/admin/tenants/${id}/suspension`, { status: draft.status });
  return <Card className="p-6 max-w-xl" data-testid="susp-tab">
    <h3 className="font-heading font-semibold mb-2">Gestione sospensione</h3>
    <p className="text-sm text-slate-500 mb-5">La modalità limitata dura 3 giorni, poi l’accesso viene bloccato. I dati restano conservati. Eliminare la sospensione riattiva l’azienda.</p>
    <AdminRecordEditor record={{ name: "Sospensione", status: t.status }} reload={reload} saveRequest={save}
      fields={[{ key: "status", label: "Stato azienda", options: [["active", "Attiva"], ["limited", "Limitata per 3 giorni"], ["blocked", "Bloccata"]] }]}
      onRemove={() => save({ status: "active" })} deleteMessage="Eliminare la sospensione e riattivare l’accesso dell’azienda?">
      <Info label="Stato attuale" value={{ active: "Attiva", limited: "Limitata", blocked: "Bloccata" }[t.status] || t.status} />
      {t.limited_until && <p className="text-sm text-slate-500">Limitata fino al {formatItalianDateTime(t.limited_until)}</p>}
    </AdminRecordEditor>
  </Card>;
}

// Aree che contengono l'archivio di base del cliente (non semplici registrazioni):
// in conferma mostrano un avviso aggiuntivo.
const ARCHIVE_WIPE_AREAS = ["ricette", "ingredienti", "lotti", "fornitori", "attrezzature"];

function PuliziaTab({ id, t }) {
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmAreas, setConfirmAreas] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/tenants/${id}/wipe-preview`).then(({ data }) => setPreview(data)).catch(() => toast.error("Errore caricamento")), [id]);
  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const openConfirm = (keys) => { if (keys.length) { setConfirmAreas(keys); setConfirmText(""); } };
  const selectedAreas = (preview?.areas || []).filter((a) => (confirmAreas || []).includes(a.key));
  const totalToDelete = selectedAreas.reduce((sum, a) => sum + a.count, 0);
  const archiveAreas = selectedAreas.filter((a) => ARCHIVE_WIPE_AREAS.includes(a.key));

  const runWipe = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/tenants/${id}/wipe`, { areas: confirmAreas, confirm_name: confirmText });
      toast.success("Dati puliti correttamente");
      setConfirmAreas(null);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!preview) return <Spinner />;

  return (
    <div className="space-y-4" data-testid="pulizia-tab">
      <Card className="border-2 border-red-200 bg-red-50/40 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 shrink-0 text-red-600" />
          <div className="text-sm text-red-800">
            <strong>Zona pericolosa.</strong> La cancellazione è immediata e non annullabile. Utenti, sedi, reparti, procedure e log di audit non vengono mai toccati da questa scheda.
          </div>
        </div>
      </Card>

      <Card className="p-4 sm:p-5">
        <h3 className="font-heading font-semibold text-navy mb-1">Aree pulibili</h3>
        <p className="mb-4 text-sm text-slate-500">Spunta solo le aree da azzerare: le altre restano intatte.</p>
        <div className="space-y-2">
          {preview.areas.map((a) => (
            <label key={a.key} className={`flex min-h-[52px] items-center gap-3 rounded-lg border p-3 ${a.count === 0 ? "cursor-not-allowed opacity-50" : "cursor-pointer"} ${selected.has(a.key) ? "border-red-400 bg-red-50" : "border-hairline bg-white"}`} data-testid={`wipe-area-${a.key}`}>
              <input type="checkbox" checked={selected.has(a.key)} disabled={a.count === 0} onChange={() => toggle(a.key)} className="h-5 w-5 accent-red-600" />
              <span className="flex-1 font-medium text-navy">{a.label}</span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold text-white ${a.count > 0 ? "bg-red-600" : "bg-slate-300"}`}>{a.count}</span>
            </label>
          ))}
        </div>
        <div className="mt-5">
          <Button variant="outline" className="w-full border-red-300 text-red-700 hover:bg-red-50" disabled={selected.size === 0} onClick={() => openConfirm([...selected])} data-testid="wipe-selected">
            <Eraser className="h-4 w-4" /> Pulisci selezionate
          </Button>
        </div>
      </Card>

      {confirmAreas && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-navy/60 p-4" role="dialog" aria-modal="true" data-testid="wipe-confirm-dialog">
          <Card className="w-full max-w-md p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-7 w-7 shrink-0 text-red-600" />
              <div>
                <h3 className="font-heading text-lg font-semibold text-navy">Confermi la pulizia?</h3>
                <p className="mt-1 text-sm text-slate-600">Stai per eliminare definitivamente <strong>{totalToDelete}</strong> record da {selectedAreas.length} {selectedAreas.length === 1 ? "area" : "aree"}:</p>
              </div>
            </div>
            <ul className="mt-3 space-y-1 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
              {selectedAreas.map((a) => <li key={a.key} className="flex justify-between"><span>{a.label}</span><span className="font-semibold">{a.count}</span></li>)}
            </ul>
            {archiveAreas.length > 0 && (
              <div className="mt-3 rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="wipe-archive-warning">
                <strong>Attenzione: stai cancellando anche l'archivio di base del cliente</strong> ({archiveAreas.map((a) => a.label).join(", ")}). Andrà ricaricato da zero. Se volevi eliminare solo le produzioni, annulla e lascia spuntata soltanto “Produzioni”.
              </div>
            )}
            <p className="mt-4 text-sm text-slate-600">Digita <strong>{t.name}</strong> per confermare:</p>
            <Input className="mt-2" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={t.name} data-testid="wipe-confirm-name" autoFocus />
            <div className="mt-5 flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={() => setConfirmAreas(null)} disabled={busy}>Annulla</Button>
              <Button variant="danger" className="flex-1" loading={busy} disabled={confirmText.trim() !== t.name.trim()} onClick={runWipe} data-testid="wipe-confirm-submit">Elimina definitivamente</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
